import { CommonModule } from '@angular/common';
import { Component, Input, OnInit, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { MenuService } from 'src/app/services/menu/menu.service';
import { ParentMenuSelectComponent } from "../parent-menu-select/parent-menu-select.component";
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TextEditorComponent } from "src/app/components/layout/text-editor/text-editor.component";
import { MetaFormModalComponent } from "src/app/components/layout/app-meta-modal/app-meta-modal.component";
import { UploadComponent } from "src/app/components/layout/file-manager/upload/upload.component";
import { CdkDragDrop, moveItemInArray, DragDropModule } from '@angular/cdk/drag-drop';
import { PagesService } from 'src/app/services/pages/pages.service';


@Component({
  selector: 'app-menu-modal',
imports: [FormsModule, CommonModule, ReactiveFormsModule, DragDropModule, UploadComponent, ParentMenuSelectComponent, TextEditorComponent, MetaFormModalComponent],  templateUrl: './menu-modal.component.html',
  styleUrl: './menu-modal.component.css'
})
export class MenuModalComponent implements OnInit, OnDestroy, OnChanges {

  @Input() menuItem: any;
  @Input() isNew: boolean = false;
  menuItemTitle: string = '';
  menuItemVisibility: number = 1;
  load = false
  apiErrors: any = {};
  form!: FormGroup;

  // --- Media (menu page attachments) ---
  currentLang: string = 'geo';
  // item_id for media refs is art_menu.page (legacy)
  editingMenuPage: number | null = null;
  galleryItems: any[] = []; // { media_id, url, pin, sort }
  gallery: string[] = [];

  // ref_type=2 reserved for menu-page attachments (list uses ref_type=1)
  readonly MEDIA_REF_TYPE_MENU = 2;

  loadingDetails = false;
  private destroy$ = new Subject<void>();

  @Input() allMenu: any = null;
  allMenuForParentSelect: any[] = [];
  filterPosForParentSelect: number = 2; // mapped via normalizePos()

  // Used by MenuManager UI to move the node locally without reloading from API
  private prevParentId: number = 0;
  private prevPos: number = 2;

  // Menu page attachments use ref_type=2
  private readonly MENU_REF_TYPE = 2;

  // Debounce saving titles per media_id (optional)
  private titleSaveTimers: Record<number, any> = {};


  // split for UI (stable arrays for cdkDropList)
  imageItemsArr: any[] = [];
  fileItemsArr: any[] = [];
  sorting = false;

  constructor(
    private fb: FormBuilder,
    public activeModal: NgbActiveModal,
    private MenuService: MenuService,
    private ToastrService: ToastrService,
    private modalService: NgbModal,
    private PagesService: PagesService,
  ) {}

  ngOnInit(): void {
    // If caller didn't set isNew correctly, infer it from presence of an id
    const hasId = !!(this.menuItem && this.menuItem.id);
    if (!hasId) {
      this.isNew = true;
    }
    
    this.form = this.fb.group({
      title: [this.menuItem?.title ?? '', [Validators.required, Validators.minLength(2)]],
      // UI keeps "active/inactive", we normalize before sending
      status: [this.menuItem?.status ?? 'active', Validators.required],
      parent: [this.menuItem?.parent ?? ''],
      disableSub: [this.menuItem?.disableSub ?? false],
      pageType: [this.menuItem?.pageType ?? '1', Validators.required],
      template: [this.menuItem?.page?.template ?? this.menuItem?.template ?? 0],
      content: [this.menuItem?.content ?? ''],
      position: [this.menuItem?.pos ?? this.menuItem?.position ?? 2, Validators.required], // 1=top,2=main,3=bottom/hidden,4=hidden (UI)
      slug: [this.menuItem?.slug ?? ''],

      isRedirect: [this.isRedirectUrl(this.menuItem?.url ?? '')],
      url: [this.menuItem?.url ?? ''],
      target: [this.normalizeTarget(this.menuItem?.target), Validators.required],

      visible: [this.menuItem?.visible ?? true],
      mainMenu: [this.menuItem?.mainMenu ?? false],
      background: [null],
      icon: [null],

      meta_keywords: this.menuItem.meta_keywords ?? '',
      meta_description: this.menuItem.meta_description ?? '',
    });

    this.setupRedirectToggle();

    // build merged menu list for parent select (if provided)
    // console.log('[MenuModal] allMenu input:', this.allMenu);
    this.allMenuForParentSelect = this.buildAllMenuForParentSelect(this.allMenu);

    // keep parent select filtered by mapped pos (old-site pos values)
    this.filterPosForParentSelect = this.normalizePos(this.form.get('position')?.value);
    // console.log('[MenuModal] initial filterPosForParentSelect:', this.filterPosForParentSelect);

    // keep parent-menu dropdown filtered by selected menu position (pos)
    this.form.get('position')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((pos) => {
        this.filterPosForParentSelect = this.normalizePos(pos);
        console.log('[MenuModal] position changed -> filterPosForParentSelect', {
          raw: pos,
          mapped: this.filterPosForParentSelect,
        });
      });

    // თუ edit რეჟიმში ვართ, მოდალმა თვითონ წამოიღოს დეტალები
    if (!this.isNew && this.menuItem?.id) {
      this.loadingDetails = true;

      // NOTE: getMenuItem უნდა დაემატოს MenuService-ში (backend-სთან ერთად)
      this.MenuService.getMenuItem(this.menuItem.id)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res: any) => {
                  
            console.log('modal data: ', this.menuItem)
            const data = res?.data ?? res;
            const pageContent = data?.page?.content ?? data?.content ?? '';
            // update local menuItem
            this.menuItem = { ...this.menuItem, ...data };

            this.currentLang = (data?.lang ?? this.menuItem?.lang ?? this.currentLang ?? 'geo').toString();
            // NOTE: backend `item()` returns `page` as an object (content/template/type),
            // while media item_id expects the numeric menu_page (art_menu.page).
            const menuPageRaw =
              // when `page` is an object
              (data?.page && typeof data.page === 'object' ? (data.page as any).menu_page : null) ??
              // sometimes returned as `page_data.menu_page`
              (data?.page_data && typeof data.page_data === 'object' ? (data.page_data as any).menu_page : null) ??
              // some endpoints may return `menu_page`
              data?.menu_page ??
              // some trees return `page` numeric
              (typeof data?.page === 'number' || typeof data?.page === 'string' ? data.page : null) ??
              // fallbacks
              this.menuItem?.page ??
              data?.id ??
              this.menuItem?.id ??
              0;

            this.editingMenuPage = Number(menuPageRaw) || null;
            console.log('[MenuModal] editingMenuPage resolved:', { menuPageRaw, editingMenuPage: this.editingMenuPage, data });
            if (this.editingMenuPage) {
              this.getGallery(this.editingMenuPage);
            }

            // patch form safely
            this.form.patchValue({
              title: data?.title ?? this.form.value.title,
              status: data?.status ?? this.form.value.status,
              parent: data?.parent_id ?? data?.parent ?? this.form.value.parent,
              // disableSub: data?.disableSub ?? data?.visible_sub_menu === 1 ?? this.form.value.disableSub,
              pageType: data?.pageType ?? data?.page?.page_type_id ?? this.form.value.pageType,
              template: data?.page?.template ?? data?.template ?? this.form.value.template,
              content: pageContent,
              position: data?.position ?? data?.pos ?? this.form.value.position,
              slug: data?.slug ?? this.form.value.slug,
              url: data?.url ?? this.form.value.url,
              isRedirect: this.isRedirectUrl(data?.url ?? ''),
              target: this.normalizeTarget(data?.target ?? data?.redirect_target ?? data?.target_id ?? data?.target),
              visible: typeof data?.visible === 'number' ? data.visible === 1 : (data?.visible ?? this.form.value.visible),
              // mainMenu: data?.mainMenu ?? data?.main === 1 ?? this.form.value.mainMenu,

              meta_keywords: this.menuItem.meta_keywords ?? '',
              meta_description: this.menuItem.meta_description ?? '',
            }, { emitEvent: false });

            // ensure validators match the loaded state
            this.applyRedirectValidators(!!this.form.get('isRedirect')?.value);

            this.loadingDetails = false;
          },
          error: () => {
            this.loadingDetails = false;
          }
        });
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['allMenu']) {
      console.log('[MenuModal] allMenu changed', {
        inputType: Array.isArray(this.allMenu) ? 'array' : typeof this.allMenu,
        keys: this.allMenu && typeof this.allMenu === 'object' && !Array.isArray(this.allMenu) ? Object.keys(this.allMenu) : null,
        arrayCount: Array.isArray(this.allMenu) ? this.allMenu.length : null,
      });

      this.allMenuForParentSelect = this.buildAllMenuForParentSelect(this.allMenu);
      console.log('[MenuModal] allMenuForParentSelect rebuilt', { count: this.allMenuForParentSelect.length });
    }
  }

  onSelectParent(parent: any) {
    const parentId = Number(parent?.id ?? parent ?? 0);
    const parentPosRaw = parent?.pos ?? parent?.position;
    const parentPos = parentPosRaw === null || parentPosRaw === undefined || parentPosRaw === ''
      ? null
      : Number(parentPosRaw);

    console.log('[MenuModal] onSelectParent', {
      parentId,
      parentPos,
      currentPosition: this.form.get('position')?.value,
      currentMenuId: this.menuItem?.id
    });

    // store selected parent id (old DB: up_id)
    this.form.patchValue({ parent: parentId });

    // If user selected a parent that belongs to a different menu position,
    // auto-align the child's position to the parent's pos.
    if (parentPos !== null && !Number.isNaN(parentPos)) {
      this.form.patchValue({ position: parentPos });
      // keep the parent selector filtered correctly
      this.filterPosForParentSelect = this.normalizePos(parentPos);
    }

    this.form.markAsDirty();
  }

  saveChanges() {
    this.apiErrors = {};

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    // ---- normalize values BEFORE sending (backend expects ints/ids) ----
    const statusVal = (this.form.get('status')?.value ?? 'active').toString().toLowerCase();
    const statusInt = (statusVal === 'active' || statusVal === '1') ? 1 : 0;
    this.form.get('status')?.setValue(statusInt, { emitEvent: false });

    // normalize UI "position" into old-site numeric `pos`
    const posInt = this.normalizePos(this.form.get('position')?.value);
    this.form.get('position')?.setValue(posInt, { emitEvent: false });

    const parentVal = this.form.get('parent')?.value;
    let isCreate = this.isNew || !this.menuItem?.id;
    // create: if parent is empty/null => root (0) for old DB up_id
    if (isCreate && (parentVal === '' || parentVal === null || parentVal === undefined)) {
      this.form.get('parent')?.setValue(0, { emitEvent: false });
    }

    this.load = true;

    // -----------------------------------------------------------------
    // UI MOVE SUPPORT (no extra API reload)
    // Remember where this menu item was before saving so the parent UI
    // can remove it from the old place and insert it into the new place.
    // -----------------------------------------------------------------
    this.prevParentId = Number(this.menuItem?.parent_id ?? this.menuItem?.up_id ?? this.menuItem?.parent ?? 0);
    this.prevPos = Number(this.menuItem?.pos ?? this.menuItem?.position ?? 2);
    console.log('[MenuModal] prev placement snapshot', { prevParentId: this.prevParentId, prevPos: this.prevPos });

    const formData = new FormData();

    // Send nested page fields for backend (ArtPage update)
    formData.append('page[content]', this.form.get('content')?.value ?? '');
    formData.append('page[page_type_id]', String(this.form.get('pageType')?.value ?? 1));
    formData.append('page[template]', String(this.form.get('template')?.value ?? 0));

    // ensure target is always '_self' or '_blank'
    this.form.get('target')?.setValue(this.normalizeTarget(this.form.get('target')?.value), { emitEvent: false });

    // If redirect is OFF, do not force URL validation/sending
    if (!this.form.get('isRedirect')?.value) {
      // keep url/target in form, but backend can ignore them if needed
    }

    Object.entries(this.form.value).forEach(([key, value]) => {

      // These are sent via nested page[...] above
      if (key === 'content' || key === 'pageType' || key === 'template') {
        return;
      }

      // backend expects `pos`, but our form control is named `position`
      if (key === 'position') {
        formData.append('pos', String(this.normalizePos(value)));
        return;
      }

      // file fields
      if (value instanceof File) {
        formData.append(key, value);
      }

      // null / undefined – skip
      else if (value === null || value === undefined) {
        return;
      }

      // booleans → string
      else if (typeof value === 'boolean') {
        formData.append(key, value ? '1' : '0');
      }

      // number → string
      else if (typeof value === 'number') {
        formData.append(key, value.toString());
      }

      // everything else → string
      else {
        // backend expects "up_id" but our form uses "parent"
        if (key === 'parent') {
          formData.append('up_id', String(value));
          return;
        }
        formData.append(key, String(value));
      }
    });

    const id = this.menuItem?.id;
    isCreate = this.isNew || !id;

    const req$ = isCreate
      ? this.MenuService.createMenuItem(formData)
      : this.MenuService.updateMenuItem(id, formData);

    req$.subscribe({
      next: (res: any) => {
        this.load = false;
        console.log(isCreate ? 'CREATE MENU ITEM RESPONSE:' : 'UPDATE MENU ITEM RESPONSE:', res);

        // Normalize backend payload so the menu tree UI can render it immediately
        const raw = (res?.data?.created ?? res?.data ?? res) as any;
        const normalized = {
          id: raw?.id,
          title: raw?.title ?? raw?.menutitle ?? '',
          slug: raw?.slug ?? '',
          url: raw?.url ?? raw?.redirect ?? '',
          target: this.normalizeTarget(raw?.target ?? raw?.redirect_target ?? raw?.target_id),
          pos: raw?.pos ?? this.normalizePos(raw?.position),
          sort: raw?.sort ?? 0,
          visible: typeof raw?.visible === 'number' ? raw.visible : (raw?.visible ? 1 : 0),
          status: typeof raw?.status === 'number' ? raw.status : (raw?.is_active ? 1 : 0),
          visible_sub_menu: raw?.visible_sub_menu ?? 0,
          show_menu: raw?.show_menu ?? 0,
          main: raw?.main ?? 0,
          // parent id in old DB is `up_id`, some endpoints return `parent_id`
          parent_id: raw?.parent_id ?? raw?.up_id ?? raw?.parent ?? 0,
          up_id: raw?.up_id ?? raw?.parent_id ?? raw?.parent ?? 0,
          // keep page linkage for admin UI
          page: raw?.page ?? raw?.menu_page ?? null,
          page_data: raw?.page_data ?? raw?.page ?? null,
          children: Array.isArray(raw?.children) ? raw.children : [],
          meta_keywords: raw?.meta_keywords ?? '',
          meta_description: raw?.meta_description ?? '',
        };

        console.log('[MenuModal] normalized payload for UI:', normalized);

        // -----------------------------------------------------------------
        // Returned payload contract for MenuManager (admin UI):
        // - _action: create|update
        // - _meta.prevParentId / _meta.prevPos: where the node was BEFORE save
        //   (used to move node locally without reloading menus)
        // -----------------------------------------------------------------
        (normalized as any)._action = isCreate ? 'create' : 'update';
        (normalized as any)._meta = {
          prevParentId: this.prevParentId,
          prevPos: this.prevPos,
        };

        this.activeModal.close(normalized);

        this.ToastrService.success('შენახულია');
      },
      error: (err: any) => {
        this.load = false;

        if (err?.error?.errors) {
          this.apiErrors = err.error.errors;
          this.form.markAllAsTouched();
        }

        console.error(isCreate ? 'CREATE MENU ITEM ERROR:' : 'UPDATE MENU ITEM ERROR:', err);
        this.ToastrService.error('შეცდომა');
      }
    });
  }

  private normalizeTarget(value: any): '_self' | '_blank' {
    // Backend may send 0/1/null or already '_self'/'_blank'
    if (value === '_blank' || value === '_self') return value;

    const v = (value ?? '').toString().trim();
    if (v === '1') return '_blank';
    if (v === '0' || v === '' || v.toLowerCase() === 'null') return '_self';

    // sometimes backend sends numeric 1/0 as number
    if (value === 1) return '_blank';
    if (value === 0) return '_self';

    // fallback
    return '_self';
  }

  private isRedirectUrl(url: string): boolean {
    const u = (url ?? '').trim();
    if (!u) return false;

    // treat absolute URLs and site-relative URLs as redirects
    if (/^https?:\/\//i.test(u)) return true;
    if (u.startsWith('/')) return true;

    // legacy patterns that are effectively redirects
    if (u.includes('/team/show/')) return true;

    // numeric-only values are NOT redirects (menu_page routing)
    if (/^\d+$/.test(u)) return false;

    // fallback: if it contains a dot-domain, treat as redirect
    if (/\.[a-z]{2,}(\/|$)/i.test(u)) return true;

    return false;
  }

  private setupRedirectToggle(): void {
    const isRedirectCtrl = this.form.get('isRedirect');
    if (!isRedirectCtrl) return;

    // initial
    this.applyRedirectValidators(!!isRedirectCtrl.value);

    // react on toggle
    isRedirectCtrl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((v) => this.applyRedirectValidators(!!v));
  }

  private applyRedirectValidators(isRedirect: boolean): void {
    const urlCtrl = this.form.get('url');
    if (!urlCtrl) return;

    if (isRedirect) {
      urlCtrl.setValidators([Validators.required]);
    } else {
      urlCtrl.clearValidators();
      // keep value; we only hide the UI when off
    }

    urlCtrl.updateValueAndValidity({ emitEvent: false });
  }

  normalizePos(value: any): number {
    // Backend expects numeric `pos` (1=top,2=main,3=hidden,10=service)
    if (value === 1 || value === 2 || value === 3 || value === 10) return value;

    const v = (value ?? '').toString().trim().toLowerCase();
    if (v === '1' || v === 'top') return 1;
    if (v === '2' || v === 'main') return 2;
    if (v === '3' || v === 'hidden') return 3;
    if (v === '10' || v === 'service' || v === 'internal') return 10;

    // default old-site main
    return 2;
  }

  private buildAllMenuForParentSelect(input: any): any[] {
    try {
      if (!input) return [];

      // If parent passed a flat array already
      if (Array.isArray(input)) {
        return input;
      }

      // If parent passed an object with sections (top/main/hidden/service/...)
      if (typeof input === 'object') {
        const keys = Object.keys(input);
        const merged: any[] = [];
        for (const k of keys) {
          const v = (input as any)[k];
          if (Array.isArray(v)) merged.push(...v);
        }
        return merged;
      }

      return [];
    } catch {
      return [];
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }


  public getMenuUploadPath(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  return `res/menu/${year}/${month}`;
}

getFullImageUrl(imagePath: string): string {
  if (!imagePath) return '';

  const value = String(imagePath).trim();
  if (!value) return '';

  if (value.startsWith('http://') || value.startsWith('https://')) {
    return value
      .replace(/^http:\/\/api\.sca\.ge\/storage\//i, 'https://api.sca.ge/api/media/')
      .replace(/^https:\/\/api\.sca\.ge\/storage\//i, 'https://api.sca.ge/api/media/')
      .replace(/^http:\/\/api\.sca\.ge/i, 'https://api.sca.ge')
      .replace(/^http:\/\//i, 'https://');
  }

  const cleanPath = value.replace(/^\/+/, '');
  return `https://api.sca.ge/api/media/${cleanPath}`;
}

getGallery(menuPage: number) {
  this.PagesService.getAdminGallery(menuPage, this.currentLang, this.MEDIA_REF_TYPE_MENU).subscribe(
    (res: any) => {
      if (res?.status === 'success' && Array.isArray(res.items)) {
        this.galleryItems = res.items || [];
        this.splitMedia();
      }
    },
    (error: any) => console.error(error)
  );
}
private isImageMedia(x: any): boolean {
  const imgExts = ['jpg','jpeg','png','webp','gif','bmp','svg'];

  const extRaw = (x?.ext ?? '').toString().toLowerCase().trim();
  if (extRaw) return imgExts.includes(extRaw);

  const mime = (x?.mime ?? x?.mimetype ?? '').toString().toLowerCase().trim();
  if (mime) return mime.startsWith('image/');

  const u = String(x?.url || x?.full_src || x?.src || '').toLowerCase();
  const clean = u.split('?')[0].split('#')[0];
  const extFromUrl = clean.includes('.') ? (clean.split('.').pop() || '') : '';
  if (extFromUrl) return imgExts.includes(extFromUrl);

  return false;
}

private splitMedia() {
  const all = Array.isArray(this.galleryItems) ? this.galleryItems : [];
  this.imageItemsArr = all.filter(x => this.isImageMedia(x));
  this.fileItemsArr  = all.filter(x => !this.isImageMedia(x));
}

// onFileUploaded(res: any) {
//   if (!this.editingMenuPage || !res?.new?.path || !res?.new?.url) return;

//   const body = {
//     item_id: this.editingMenuPage,
//     lang: this.currentLang,
//     ref_type: this.MEDIA_REF_TYPE_MENU,
//     src: res.new.path,
//     full_src: res.new.url,
//     orig_name: res.filename,
//     ext: res.filename?.split('.').pop() || '',
//     type: 1,
//     pin: 0,
//   };

//   this.PagesService.attachMedia(body).subscribe({
//     next: (r: any) => {
//       if (r?.status === 'success') this.getGallery(this.editingMenuPage as number);
//     },
//     error: (err: any) => {
//       console.error('Attach media error:', err);
//       this.ToastrService.error('ფაილის მიბმის შეცდომა');
//     },
//   });
// }
onFileUploaded(res: any) {
  if (!this.editingMenuPage) return;

  const path: string | undefined = res?.path;
  const fullSrc: string | undefined = res?.url || res?.path;
  const filename: string = res?.filename || '';

  if (!path || !fullSrc) return;

  const ext = (filename.split('.').pop() || '').toLowerCase();
  const isImg = ['jpg','jpeg','png','webp','gif','bmp','svg'].includes(ext);

  const body = {
    item_id: this.editingMenuPage,
    lang: this.currentLang,
    ref_type: this.MEDIA_REF_TYPE_MENU,
    src: path,
    full_src: fullSrc,
    orig_name: filename,
    ext,
    type: isImg ? 1 : 2,
    pin: 0,
  };

  this.PagesService.attachMedia(body).subscribe({
    next: (r: any) => {
      if (r?.status === 'success') this.getGallery(this.editingMenuPage as number);
    },
    error: (err: any) => {
      console.error('Attach media error:', err);
      this.ToastrService.error('ფაილის მიბმის შეცდომა');
    },
  });
}

setMainImage(img: any) {
  if (!this.editingMenuPage) return;

  const body = {
    item_id: this.editingMenuPage,
    lang: this.currentLang,
    ref_type: this.MEDIA_REF_TYPE_MENU,
    media_id: img.media_id,
  };

  this.PagesService.pinMedia(body).subscribe({
    next: () => this.getGallery(this.editingMenuPage as number),
    error: () => this.ToastrService.error('მთავარი ფაილის დაყენება ვერ მოხერხდა'),
  });
}

removeGalleryImage(index: number) {
  const item = this.galleryItems[index];
  if (!item || !this.editingMenuPage) return;

  this.PagesService.detachMedia({
    item_id: this.editingMenuPage,
    lang: this.currentLang,
    ref_type: this.MEDIA_REF_TYPE_MENU,
    media_id: item.media_id,
    delete_file: false,
  }).subscribe({
    next: () => {
      this.galleryItems.splice(index, 1);
      this.gallery = this.galleryItems.map((x) => x.url);
    },
    error: (err: any) => {
      console.error('Detach media error:', err);
      this.ToastrService.error('ფაილის წაშლა ვერ მოხერხდა');
    },
  });
}

removeMedia(item: any, ev?: Event) {
  ev?.preventDefault();
  ev?.stopPropagation();

  if (!this.editingMenuPage || !item?.media_id) return;

  this.PagesService.detachMedia({
    item_id: this.editingMenuPage,
    lang: this.currentLang,
    ref_type: this.MEDIA_REF_TYPE_MENU, // 2
    media_id: Number(item.media_id),
    delete_file: false,
  }).subscribe({
    next: () => {
      const mid = Number(item.media_id);
      this.galleryItems = (this.galleryItems || []).filter(x => Number(x?.media_id) !== mid);
      this.splitMedia();
      this.ToastrService.success('წაიშალა');
    },
    error: (err: any) => {
      console.error('Detach media error:', err);
      this.ToastrService.error('წაშლა ვერ მოხერხდა');
    }
  });
}

dropImages(event: CdkDragDrop<any[]>) {
  if (!this.editingMenuPage) return;

  moveItemInArray(this.imageItemsArr, event.previousIndex, event.currentIndex);
  this.galleryItems = [...this.imageItemsArr, ...this.fileItemsArr];
  this.persistSort();
}

dropFiles(event: CdkDragDrop<any[]>) {
  if (!this.editingMenuPage) return;

  moveItemInArray(this.fileItemsArr, event.previousIndex, event.currentIndex);
  this.galleryItems = [...this.imageItemsArr, ...this.fileItemsArr];
  this.persistSort();
}

private persistSort() {
  if (!this.editingMenuPage) return;

  const orderedIds = (this.galleryItems || [])
    .map(x => Number(x?.media_id))
    .filter(Boolean);

  if (!orderedIds.length) return;

  this.sorting = true;

  this.PagesService.reorderMedia({
    item_id: this.editingMenuPage,
    lang: this.currentLang,
    ref_type: this.MENU_REF_TYPE, // 2
    ordered_media_ids: orderedIds
  }).subscribe({
    next: () => {
      this.sorting = false;
      this.splitMedia();
      this.ToastrService.success('დალაგება შენახულია');
    },
    error: (err: any) => {
      console.error('reorderMedia error', err);
      this.sorting = false;
      this.ToastrService.error('დალაგება ვერ შეინახა');
    }
  });
}

isImageFile(url: string | null | undefined): boolean {
  const u = (url ?? '').toLowerCase();
  return (
    u.endsWith('.png') ||
    u.endsWith('.jpg') ||
    u.endsWith('.jpeg') ||
    u.endsWith('.webp') ||
    u.endsWith('.gif')
  );
}



renameFile(file: any, ev?: Event) {
  ev?.preventDefault();
  ev?.stopPropagation();

  if (!this.editingMenuPage || !file?.media_id) return;

  const current = (file.title || file.orig_name || '').toString();
  const nextTitle = window.prompt('ფაილის სახელის შეცვლა', current);
  if (nextTitle === null) return;

  const title = nextTitle.trim();
  if (!title.length) {
    this.ToastrService.warning('სახელი ცარიელია');
    return;
  }

  const payload = {
    item_id: this.editingMenuPage,
    lang: this.currentLang,
    ref_type: this.MEDIA_REF_TYPE_MENU, // 2
    media_id: Number(file.media_id),
    title,
  };

  this.PagesService.updateAdminMediaTitle(payload).subscribe({
    next: (res: any) => {
      // ლოკალურად განვაახლოთ, რომ სიაშიც შეცვლილი გამოჩნდეს
      const mid = Number(file.media_id);

      const apply = (arr: any[]) => {
        const idx = (arr || []).findIndex(x => Number(x?.media_id) === mid);
        if (idx >= 0) arr[idx] = { ...arr[idx], title };
      };

      apply(this.galleryItems);
      apply(this.imageItemsArr);
      apply(this.fileItemsArr);

      this.ToastrService.success('შენახულია');
    },
    error: (err: any) => {
      console.error('updateAdminMediaTitle error:', err);
      this.ToastrService.error('შენახვა ვერ მოხერხდა');
    }
  });
}

saveMediaTitle(img: any) {
  if (!this.editingMenuPage || !img?.media_id) return;

  const title = String(img.title ?? '').trim();

  this.PagesService.updateAdminMediaTitle({
    item_id: this.editingMenuPage,
    lang: this.currentLang,
    ref_type: this.MENU_REF_TYPE,
    media_id: img.media_id,
    title
  }).subscribe({
    next: () => this.ToastrService.success('სათაური შენახულია'),
    error: (err: any) => {
      console.error('updateAdminMediaTitle error', err);
      this.ToastrService.error('სათაურის შენახვა ვერ მოხერხდა');
    }
  });
}

}
